import { DocumentCard } from '@zorro/design-system/v2/DocumentCard';
import { Dropzone } from '@zorro/design-system/v2/Dropzone';
import { formatDateEnLocale, getNow } from '@zorro/shared/formatters';
import { Stack, Text } from '@zorro/web/design-system';
import {
  downloadBenefitDocument,
  showErrorNotification,
  useMonolithMutation,
} from '@zorro/web/ui-utils';

import { useAnalytics } from '../Analytics';
import {
  UploadableBenefitDocument,
  getDocumentKey,
  isPersistedDocument,
} from '../BenefitDocuments';

type Props = {
  onboardingPeriodId: string;
  documents: UploadableBenefitDocument[];
  subtitle: string;
  dropzoneTitle: string;
  onChange: (documents: UploadableBenefitDocument[]) => void;
};

export const ProofOfConsentSection = ({
  onboardingPeriodId,
  documents,
  subtitle,
  dropzoneTitle,
  onChange,
}: Props) => {
  const { sendEvent } = useAnalytics();
  const { tryMutate: tryRemoveDocument } = useMonolithMutation({
    method: 'benefitDocumentsControllerRemoveBenefitDocument',
    shouldReloadPage: false,
    shouldShowLoadingOverlay: false,
    shouldInvalidateQueries: false,
  });

  const handleDropDocuments = (files: File[]) => {
    onChange([...documents, ...files]);
    void sendEvent('upload_proof_of_consent', {
      onboarding_period_id: onboardingPeriodId,
      number_of_files: files.length,
    });
  };

  const handleDeleteDocument = async (document: UploadableBenefitDocument) => {
    if (isPersistedDocument(document)) {
      const removeResult = await tryRemoveDocument([document.id]);
      if (removeResult.isErr()) return;
    }
    onChange(documents.filter((item) => item !== document));

    await sendEvent('remove_proof_of_consent', {
      onboarding_period_id: onboardingPeriodId,
      was_persisted: isPersistedDocument(document),
    });
  };

  const handleDownloadDocument = async (
    document: Exclude<UploadableBenefitDocument, File>
  ) => {
    await downloadBenefitDocument(document.documentUrl, document.fileName);

    await sendEvent('download_proof_of_consent', {
      onboarding_period_id: onboardingPeriodId,
    });
  };

  return (
    <Stack gap="md">
      <Stack gap={0}>
        <Text size="md" fw={600}>
          Proof of consent
        </Text>
        <Text size="sm" c="var(--color-text-default-secondary)">
          {subtitle}
        </Text>
      </Stack>
      <Stack gap="sm">
        <Dropzone
          multiple
          onDrop={handleDropDocuments}
          onReject={(files) => {
            const firstError = files[0]?.errors[0];
            if (firstError) {
              showErrorNotification({
                message: firstError.message,
                title: firstError.code,
              });
            }
          }}
        >
          <Dropzone.Empty title={dropzoneTitle} subtitle="Or drag it here" />
        </Dropzone>
        {documents.map((document) => (
          <DocumentCard
            key={getDocumentKey(document)}
            fileName={
              document instanceof File ? document.name : document.fileName
            }
            variant="shadow"
            date={formatDateEnLocale(
              isPersistedDocument(document) ? document.createdAt : getNow()
            )}
            onDownload={
              document instanceof File
                ? undefined
                : () => void handleDownloadDocument(document)
            }
            onDelete={() => void handleDeleteDocument(document)}
          />
        ))}
      </Stack>
    </Stack>
  );
};
